import React, { MouseEvent, useCallback, useContext, useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import cn from "classnames";
import { DrawerProps } from "@mui/material/Drawer/Drawer";
import { sidePanelContext } from "../../lib";
import { Icon } from "../icons/icon";
import styles from "./side-panel.module.scss";

export interface ISidePanel {
    isOpen: boolean;
    openSidePanel: (children: React.ReactElement, pPosition?: DrawerProps["anchor"], pSize?: number, showClose?: boolean) => void;
    close: () => void;
    childComponent?: React.ReactElement;
    panelPosition: DrawerProps["anchor"];
    panelSize: number;
    showCloseBtn: boolean;

}

export function useSidePanel(): ISidePanel {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [showCloseBtn, setShowCloseBtn] = useState<boolean>(true);
    const [panelSize, setPanelSize] = useState<number>(500);
    const [panelPosition, setPanelPosition] = useState<DrawerProps["anchor"]>("left");
    const [childComponent, setChildComponent] = useState<React.ReactElement>();

    const close = () => {
        setIsOpen(false);
        setChildComponent(undefined);
    };

    const openSidePanel = (component: React.ReactElement, pPosition: DrawerProps["anchor"] = "left", pSize = 500, showClose = true) => {
        setChildComponent(component);
        setPanelSize(pSize);
        setPanelPosition(pPosition);
        setIsOpen(true);
        setShowCloseBtn(showClose);
    };

    return { isOpen, close, openSidePanel, childComponent, panelSize, panelPosition, showCloseBtn };
}

const minDrawerWidth = 200;
const maxDrawerWidth = 1000;

export const SidePanel: React.FC = React.memo(() => {
    const { isOpen, close, childComponent, panelSize, panelPosition, showCloseBtn } = useContext(sidePanelContext);
    const [drawerWidth, setDrawerWidth] = React.useState(0);

    useEffect(() => {
        if (isOpen) {
            setDrawerWidth(panelSize);
        } else {
            setDrawerWidth(0);
        }
    }, [isOpen]);

    const handleMouseDown = (e: MouseEvent) => {
        document.addEventListener("mouseup", handleMouseUp, true);
        document.addEventListener("mousemove", handleMouseMove, true);
    };

    const handleMouseUp = () => {
        document.removeEventListener("mouseup", handleMouseUp, true);
        document.removeEventListener("mousemove", handleMouseMove, true);
    };

    const handleMouseMove = useCallback((e: any) => {
        let newWidth;
        if (panelPosition === "left") {
            newWidth = e.clientX - document.body.offsetLeft;
        } else {
            newWidth = document.body.clientWidth - e.clientX;
        }

        if (newWidth > minDrawerWidth && newWidth < maxDrawerWidth) {
            setDrawerWidth(newWidth);
        }
    }, [panelPosition]);

    return (
        isOpen ?
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: "none", sm: "block" },
                    "& .MuiDrawer-paper": {
                        backgroundColor: "var(--hr-color-surface-100)",
                        marginTop: "64px",
                        boxSizing: "border-box",
                        height: "calc(100% - 64px)",
                        width: drawerWidth
                    },
                }}
                open={isOpen}
                anchor={panelPosition}
            >
                <div className={styles.sidebarContainer}>
                    <div onMouseDown={e => handleMouseDown(e)}
                         className={cn(styles.dragger, panelPosition === "left" ? styles.draggerLeft : styles.draggerRight)}/>
                    {showCloseBtn && <div className={styles.sidebarActionItem}>
                        <Icon glyph="close" size="l" onClick={close}/>
                    </div>}
                    <div className={styles.sidebarContent}>
                        {childComponent}
                    </div>
                </div>
            </Drawer>
            : null
    );
});
